import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { HeartbeatPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'
import { breadcrumbList, jsonLdScript, webPage } from '@/lib/schema'

export const Route = createFileRoute('/_site/features/heartbeat-monitoring')({
  component: HeartbeatMonitoringPage,
  head: () => ({
    meta: [
      { title: 'Heartbeat Monitoring — Cron Job Monitor | Uptrack' },
      { name: 'description', content: 'Monitor cron jobs and background workers with heartbeat checks. Alerted when a job misses its ping. No add-on fees.' },
      { property: 'og:title', content: 'Heartbeat Monitoring — Cron Job Monitor | Uptrack' },
      { property: 'og:description', content: 'Monitor cron jobs and scheduled tasks. Get alerted when jobs fail to report in. Included with every paid plan.' },
      { property: 'og:image', content: 'https://uptrack.app/og/features-heartbeat-monitoring.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/features/heartbeat-monitoring' },
    ],
    scripts: [
      jsonLdScript(breadcrumbList([
        { name: 'Home', url: 'https://uptrack.app' },
        { name: 'Features', url: 'https://uptrack.app/features' },
        { name: 'Cron & Heartbeat Monitoring', url: 'https://uptrack.app/features/heartbeat-monitoring' },
      ])),
      jsonLdScript(webPage({
        name: 'Uptrack Heartbeat Monitoring',
        description: 'Cron job and scheduled task monitoring with configurable grace periods and passive heartbeat checks.',
        url: 'https://uptrack.app/features/heartbeat-monitoring',
      })),
    ],
  }),
})

const competitors = [
  { name: 'UptimeRobot', heartbeat: 'Paid add-on', gracePeriod: 'Fixed', freeIncluded: 'No' },
  { name: 'Better Stack', heartbeat: 'Yes', gracePeriod: 'Yes', freeIncluded: 'Limited' },
  { name: 'Cronitor', heartbeat: 'Core feature', gracePeriod: 'Yes', freeIncluded: '5 monitors' },
  { name: 'Uptrack', heartbeat: 'Yes', gracePeriod: 'Configurable', freeIncluded: 'Paid plans', highlight: true },
]

function HeartbeatMonitoringPage() {
  return (
    <FeaturePage
      section="Heartbeat / cron job monitoring"
      title="Heartbeat monitoring for cron jobs and workers."
      intro="Your cron jobs and scheduled tasks ping Uptrack when they run. If a ping does not arrive within the grace period, you get alerted. No agents to install."
      featuresHeading="Know when your jobs stop running"
      features={[
        { title: 'Passive monitoring', desc: 'Your job pings a unique URL when it completes. If the ping stops arriving, Uptrack alerts you.' },
        { title: 'Configurable grace period', desc: 'Set how long to wait before alerting. A 5-minute job with a 10-minute grace period avoids false alarms from slow runs.' },
        { title: 'One-line integration', desc: 'Add a single curl call to the end of your script. Works with cron, systemd timers, Kubernetes CronJobs, and any scheduler.' },
        { title: 'Included — competitors charge extra', desc: 'Heartbeat monitoring is included on every paid Uptrack plan. No add-on fees, no separate product.' },
      ]}
      table={{
        heading: 'Uptrack vs the alternatives',
        intro: 'Most uptime tools charge extra for heartbeat monitoring or limit it to paid plans.',
        columns: ['Provider', 'Heartbeat', 'Grace Period', 'Included'],
        rows: competitors.map((c) => ({ cells: [c.name, c.heartbeat, c.gracePeriod, c.freeIncluded], highlight: c.highlight })),
      }}
      why={{
        heading: 'How heartbeat monitoring works',
        body: 'Create a heartbeat monitor in Uptrack and get a unique ping URL. Add curl -fsS https://api.uptrack.app/api/heartbeat/YOUR_TOKEN to the end of your script. Uptrack expects a ping within the configured interval plus grace period. If the ping is late or missing, your alert channels fire.',
      }}
      cta={{ heading: 'Monitor your cron jobs now', body: 'Every paid plan includes 30-second checks and alert confirmation.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard/heartbeats">
          <HeartbeatPiece />
        </ProductFrame>
      }
    />
  )
}
